"use client";

import { Headset, LinkIcon } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { Link, List, Stack, Text } from "@leabankab/component-library";
import { StepContainer } from "@/components/layout";
import { Heading } from "@/components/ui";
import { type SupportedLocale, getLocaleConfig } from "@/config/locale-config";
import { SigningErrorFooter } from "./signing-error-footer";
import { SigningErrorIcon } from "./signing-error-icon";

type Props = {
	errorCode?: string;
};

/**
 * Shown when the identity provider round-trip failed and no flow id could be
 * recovered, so there is nothing to retry - the customer has to reopen their
 * original link. `linkLost` is Swedish-only for now, hence the `has` guards.
 */
export const SigningLinkLost = ({ errorCode }: Props) => {
	const t = useTranslations("pages.SignCallbackPage");
	const locale = useLocale() as SupportedLocale;
	const { supportPhone: phone, supportPhoneHref } = getLocaleConfig(locale);

	const hasSpecificCopy = t.has("linkLost.title");
	const title = hasSpecificCopy
		? t("linkLost.title")
		: t("signingFailed.title");
	const description = hasSpecificCopy
		? t("linkLost.description")
		: t("signingFailed.errorDescriptions.default");
	const whatYouCanDo = hasSpecificCopy
		? (t.raw("linkLost.whatYouCanDo") as string[])
		: [];

	const actionIcons = [
		<LinkIcon key="reopen" size={20} aria-hidden="true" />,
		<Headset key="support" size={20} aria-hidden="true" />,
	];

	return (
		<StepContainer>
			<Stack gap={3} align="stretch">
				<Stack gap={2} align="center" className="text-center text-balance">
					<SigningErrorIcon>
						<LinkIcon size={40} strokeWidth={2} aria-hidden="true" />
					</SigningErrorIcon>
					<Heading level={1} textStyle="heading-lg">
						{title}
					</Heading>
					<Text color="text.subtle">{description}</Text>
				</Stack>

				{errorCode && (
					<div className="rounded-xl bg-bg-subtle p-2.5">
						<Text textStyle="body-sm" fontWeight="medium" color="text.base">
							{t("signingFailed.errorCodeLabel", { code: errorCode })}
						</Text>
					</div>
				)}

				{whatYouCanDo.length > 0 && (
					<Stack gap={1.5}>
						<Heading level={2} textStyle="heading-xs">
							{t("linkLost.whatYouCanDoTitle")}
						</Heading>
						<List.Root className="bg-transparent p-0">
							{whatYouCanDo.map((item, index) => (
								<List.Item key={item} icon={actionIcons[index]}>
									{item.replace("{phone}", phone)}
								</List.Item>
							))}
						</List.Root>
					</Stack>
				)}

				<Stack gap={1.5} align="center">
					<Link href={supportPhoneHref} color="primary" className="font-medium">
						{t("signingFailed.contactSupport")}
					</Link>
				</Stack>

				{whatYouCanDo.length === 0 && (
					<SigningErrorFooter text={t("footer", { phone })} />
				)}
			</Stack>
		</StepContainer>
	);
};
